import React, { createContext, ReactChild, useContext, useEffect, useReducer } from "react";
import { ProductBuilderOptions } from "../types/ProductBuilderTypes";
import { getSelections } from "../utils/defaultSelections";
import Quantity from "../utils/Quantity";
import { Logger } from "../logging/logger";
import { IdentityContext } from "./IdentityProvider";
import { PricingContext } from "./PricingContext";
import { PageStaticDataContext } from "./PageStaticDataContextProvider";

export interface ProductContextType extends ProductState {
  selectCompatibleValue: (newSelection: Record<string, string | null | undefined>) => void;
  selectIncompatibleValue: (newSelection: Record<string, string | null | undefined>) => void;
  selectQuantity: (newQuantity: number) => void;
  setClientSideStateInitialized: (clientSideStateInitialized: boolean) => void;
  setUpdateInProgress: (updateInProgress: boolean) => void;
  selectDesignOptions: (designOptions: Record<string, string>) => void;
}

export const ProductContext = createContext<ProductContextType>({
  initialSelections: {},
  initialQuantity: 0,
  showReferentialImage: false,
  selections: undefined,
  selectedQuantity: 0,
  clientSideStateInitialized: false,
  updateInProgress: true,
  designOptions: {},
  selectCompatibleValue: () => {},
  selectIncompatibleValue: () => {},
  selectQuantity: () => {},
  setClientSideStateInitialized: () => {},
  setUpdateInProgress: () => {},
  selectDesignOptions: () => {},
});

export interface ProductContextProviderProps {
  productBuilderOptions: ProductBuilderOptions;
  children?: ReactChild | ReactChild[] | undefined | null;
}

interface ProductState {
  selections: Record<string, string> | undefined;
  selectedQuantity: number;
  clientSideStateInitialized: boolean;
  initialSelections: Record<string, string>;
  initialQuantity: number;
  showReferentialImage: boolean;
  updateInProgress: boolean;
  designOptions: Record<string, string>;
}

const ProductContextProvider = (props: ProductContextProviderProps) => {
  const { productBuilderOptions, children } = props;
  const { isIdentityInitialized } = useContext(IdentityContext);
  const { isPricingContextInitialized } = useContext(PricingContext);
  const { mpv, country } = useContext(PageStaticDataContext);
  const initialQuantity = Quantity.getDefaultQuantity(
    mpv.quantities?.default,
    mpv.quantities?.recommended
  );

  const initialState: ProductState = {
    showReferentialImage: false,
    selections: undefined,
    selectedQuantity: initialQuantity,
    initialQuantity,
    clientSideStateInitialized: false,
    initialSelections: {},
    updateInProgress: true,
    designOptions: {},
  };
  const productContextReducer = (state: ProductState, action: any) => {
    switch (action.type) {
      case "updateSelections": {
        return {
          ...state,
          selections: action.selections,
          showReferentialImage: true,
          updateInProgress: true,
        };
      }
      case "updateSelectedQuantity": {
        return {
          ...state,
          selectedQuantity: action.selectedQuantity,
          updateInProgress: true,
        };
      }
      case "updateDesignOptions": {
        return {
          ...state,
          designOptions: action.designOptions,
          updateInProgress: true,
        };
      }
      case "setClientSideStateInitialized": {
        return {
          ...state,
          clientSideStateInitialized: action.clientSideStateInitialized,
        };
      }
      case "setInitialSelections": {
        return {
          ...state,
          selections: action.selections,
          initialSelections: action.selections,
          updateInProgress: true,
        };
      }
      case "setUpdateInProgress": {
        return {
          ...state,
          updateInProgress: action.updateInProgress,
        };
      }
      default: {
        Logger.instance.error("Unknown action in product state", {
          actionType: action.type,
        });
        return { ...state };
      }
    }
  };

  const [state, dispatch] = useReducer(productContextReducer, initialState);
  const {
    initialSelections,
    designOptions,
    selections,
    selectedQuantity,
    clientSideStateInitialized,
    showReferentialImage,
    updateInProgress,
  } = state;
  const selectCompatibleValue = (newSelection: Record<string, string | null | undefined>) => {
    const newSelections = { ...selections, ...newSelection };
    const selectionsWithoutEmpty = Object.fromEntries(
      Object.entries(newSelections).filter(([_, v]) => v !== null && v !== undefined)
    );
    dispatch({
      type: "updateSelections",
      selections: {
        ...selectionsWithoutEmpty,
      },
    });
  };

  const selectIncompatibleValue = (newSelection: Record<string, string | null | undefined>) => {
    const newSelections = { ...newSelection };
    const selectionsWithoutEmpty = Object.fromEntries(
      Object.entries(newSelections).filter(([_, v]) => v !== null || v !== undefined)
    );
    dispatch({
      type: "updateSelections",
      selections: {
        ...selectionsWithoutEmpty,
      },
    });
  };
  const selectQuantity = (newQuantity: number) => {
    dispatch({
      type: "updateSelectedQuantity",
      selectedQuantity: newQuantity,
    });
  };
  const selectDesignOptions = (newDesignOptions: Record<string, string>) => {
    dispatch({
      type: "updateDesignOptions",
      designOptions: newDesignOptions,
    });
  };
  const setClientSideStateInitialized = (clientSideStateInitialized: boolean) => {
    dispatch({
      type: "setClientSideStateInitialized",
      clientSideStateInitialized,
    });
  };
  const setInitialSelections = (selections: Record<string, string>) => {
    dispatch({
      type: "setInitialSelections",
      selections,
    });
  };
  const setUpdateInProgress = (updateInProgress: boolean) => {
    dispatch({
      type: "setUpdateInProgress",
      updateInProgress,
    });
  };

  useEffect(() => {
    let mounted = true;
    const getInitialSelections = async () => {
      if (isIdentityInitialized && isPricingContextInitialized) {
        const resolvedInitialSelections = await getSelections({
          productBuilderOptions,
          mpv,
          country,
        });
        if (mounted) {
          setInitialSelections(resolvedInitialSelections);
        }
      }
    };
    getInitialSelections();
    return () => {
      mounted = false;
    };
  }, [mpv, country, productBuilderOptions, isIdentityInitialized, isPricingContextInitialized]);

  return (
    <ProductContext.Provider
      value={{
        showReferentialImage,
        initialSelections,
        initialQuantity,
        selections,
        selectedQuantity,
        clientSideStateInitialized,
        updateInProgress,
        designOptions,
        selectCompatibleValue,
        selectIncompatibleValue,
        selectQuantity,
        setClientSideStateInitialized,
        setUpdateInProgress,
        selectDesignOptions,
      }}
    >
      {children}
    </ProductContext.Provider>
  );
};

export default ProductContextProvider;
